<template>
    <common-dropdown
        :isOpen="isOpen"
        :bgColor="dropdownBgColor"
        :recalculatePosition="false"
        arrowPositionY="top"
        arrowPositionX="right"
    >
        <div class="app-user-dropdown">
            <div class="app-user-dropdown__body">
                <template v-if="isAdminOrPhysician">
                    <div class="app-user-dropdown__filter">
                        <div class="app-user-dropdown__field">
                            <label>{{ $t('layouts.app.user_menu.activate_physician') }}</label>
                            <text-field
                                :placeholder="$t('layouts.app.user_menu.search_member', { practice: practiceName })"
                                :hasAutofocus="true"
                                :formData="data"
                                valuePath="query"
                                @change="change($event)"
                            ></text-field>
                        </div>

                        <div class="app-user-dropdown__field">
                            <radio-field
                                :options="scopeOptions"
                                :formData="data"
                                valuePath="scope"
                                @change="change($event)"
                            ></radio-field>
                        </div>
                    </div>

                    <div ref="results" class="app-user-dropdown__results">
                        <common-member-switch
                            :isProcessing="isSending"
                            :members="displayedMembersList"
                            :switchToLabel="$t('common.users_list.switch_to')"
                        ></common-member-switch>
                    </div>
                </template>
                <common-dropdown-items :menuItems="menuItems" @item-clicked="itemClicked"></common-dropdown-items>
            </div>
        </div>
    </common-dropdown>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import PracticeMembersAPI from 'api/PracticeMembersAPI'
    import UserRolesEnum from '../../../enums/UserRolesEnum'

    export default {
        mixins: [FormMixin],

        props: {
            isOpen: { type: Boolean, default: false },
            isOnlyLogoutAvailable: { type: Boolean, default: false },
        },

        data() {
            return {
                members: [],
                data: {
                    scope: 'my',
                },
                activeMember: this.$auth.getActiveMember(),
            }
        },

        computed: {
            menuItems() {
                const items = [
                    {
                        name: this.$t('layouts.app.user_menu.logout'),
                        payload: { route: 'auth.login' },
                    },
                ]

                if (this.shouldShowManageProfileOption && !this.isOnlyLogoutAvailable) {
                    items.unshift({
                        name: this.physicianProfileManagement,
                        payload: { route: 'profile' },
                    })
                }

                return items
            },

            stopProcessingAfterRequestFinished() {
                return true
            },

            isAdminOrPhysician() {
                return (
                    !this.$auth.isUserOnlyPhysician() &&
                    !this.$auth.isUserAdministrator() &&
                    !this.$auth.isPracticeAncillary() &&
                    !this.$auth.isUserTemporaryPhysician() &&
                    !this.isOnlyLogoutAvailable
                )
            },

            dropdownBgColor() {
                return this.$auth.isUserPhysician() || this.isOnlyLogoutAvailable ? 'white' : 'gray'
            },

            scopeOptions() {
                return ['my', 'all'].map((value) => ({
                    label: this.$t(`layouts.app.user_menu.location_scope.${value}`),
                    value,
                }))
            },

            practiceName() {
                return this.$auth.getPractice().name
            },

            displayedMembersList() {
                return this.data.query ? this.members : this.$auth.getMembers()
            },

            physicianProfileManagement() {
                if (this.$auth.getActiveMember().id !== this.$auth.getUser().id) {
                    return this.$t('layouts.app.user_menu.profile.manage_account', {
                        physicianFirstName: this.activeMember.firstName,
                        physicianLastName: this.activeMember.lastName,
                    })
                }
                return this.$t('layouts.app.user_menu.profile.my_profile')
            },

            shouldShowManageProfileOption() {
                return this.$auth.isUserPhysician() || this.$auth.isMasterUser()
            },
        },

        methods: {
            searchPhysicians(data = {}) {
                this.handleRequest(
                    PracticeMembersAPI.getList(this.$auth.getPractice().id, {
                        query: data.query,
                        userRoleId: UserRolesEnum.PHYSICIAN.ID,
                    }),
                ).then((members) => {
                    this.members = members
                })
            },

            logOut() {
                this.$auth.logOut()
                this.$router.push({ name: 'auth.login' })
            },

            goToProfessionalProfile() {
                if (this.$route.name !== 'profile') {
                    this.$router.push({ name: 'profile' })
                }
            },

            itemClicked(payload) {
                if (payload.route === 'profile') {
                    this.goToProfessionalProfile()
                }

                if (payload.route === 'auth.login') {
                    this.logOut()
                }
            },
        },

        mounted() {
            this.$on('local:afterChange', () => this.searchPhysicians(this.data))
        },
    }
</script>
